import { useCallback, useEffect, useMemo, useState } from 'react';
import percent from '~/assets/icons/percent.svg';
import userRound from '~/assets/icons/user_round.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { BankOperationType, Currency } from '~/shared';
import { BankStages } from '../../models/Bank';
import {
	OverlayType,
	setActiveBankState,
	setOverlayTransferData,
	setOverlayType,
	setOverlayVisible,
	useLayoutDispatch,
} from '../../redux';
import { AppBackButton } from '../AppBackButton';
import { AppBankCards } from '../AppBankCards';
import { CardInput } from '../CardInput';
import { MoneyInput } from '../MoneyInput';
import {
	StyledButtonContainer,
	StyledContainer,
	StyledHead,
	StyledInfoContainer,
	StyledInfoRow,
	StyledInputContainer,
	StyledMarginContainer,
} from './AppBankTransfer.styled';

const AppBankTransfer = () => {
	const dispatch = useLayoutDispatch();
	const [cardNumber, setCardNumber] = useState<string>('');
	const [cardOwner, setCardOwner] = useState<string | null>(null);
	const [isError, setIsError] = useState<boolean>(false);
	const [amount, setAmount] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	const fee = useObservable(
		rage.bank.getTransferFee(BankOperationType.TRANSFER),
	);

	const buttonText = useMemo(() => {
		if (isError) {
			return 'Неверный номер карты';
		}

		if (cardNumber.length !== 16 || !cardOwner) {
			return 'Введите номер карты';
		}

		if (amount === 0) {
			return 'Введите сумму';
		}

		return 'Перевести деньги';
	}, [cardNumber, cardOwner, amount, isError]);

	const transfer = useCallback(async () => {
		setIsLoading(true);
		dispatch(setOverlayType(OverlayType.Transfer));
		dispatch(
			setOverlayTransferData({
				amount: amount,
				player: cardOwner ?? '',
				date: Date.now(),
			}),
		);
		try {
			if (cardNumber && cardNumber.length === 16 && cardOwner) {
				await rage.bank.transfer(cardNumber, amount);
				dispatch(setOverlayVisible(true));
			}
		} catch (e) {
			// @TODO: какой-то вывод ошибки
		} finally {
			setIsLoading(false);
		}
	}, [cardNumber, cardOwner, amount]);

	useEffect(() => {
		if (cardNumber.length < 16) {
			setCardOwner(null);
			setIsError(false);
		}

		if (cardNumber.length === 16) {
			setIsError(false);
			rage.bank
				.getCardOwner(cardNumber)
				.then((value) => {
					if (value) {
						setCardOwner(value);
					} else {
						setIsError(true);
						setCardOwner(null);
					}
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [cardNumber]);

	return (
		<div>
			<StyledHead>
				<AppBackButton
					onClick={() => dispatch(setActiveBankState(BankStages.MAIN))}
				/>
				<Text>Переводы</Text>
			</StyledHead>
			<StyledContainer>
				<AppBankCards compact />
				<StyledMarginContainer>
					<CardInput
						value={cardNumber}
						onChange={setCardNumber}
						error={isError}
					/>
				</StyledMarginContainer>
				<StyledInputContainer>
					<MoneyInput
						value={amount}
						changeHandler={setAmount}
						placeholder="Введите сумму"
					/>
				</StyledInputContainer>
				<StyledInfoContainer>
					{cardOwner ? (
						<StyledInfoRow>
							<Text>Имя получателя</Text>
							<Text>{cardOwner}</Text>
							<img src={userRound} />
						</StyledInfoRow>
					) : null}
					<StyledInfoRow>
						<Text>Вы заплатите</Text>
						<Text>
							{Math.floor(amount + amount * fee).toLocaleString('ru-RU')}
						</Text>
						<CurrencyIcon icon={rubleIcon} size="sm" />
					</StyledInfoRow>
					{fee ? (
						<StyledInfoRow>
							<Text>Комиссия банка</Text>
							<Text>{(fee * 100).toLocaleString('ru-RU')}</Text>
							<img src={percent} />
						</StyledInfoRow>
					) : null}
				</StyledInfoContainer>
				<StyledButtonContainer>
					<Button
						color={ButtonColors.Select}
						height={ButtonHeight.Height60}
						disabled={
							isLoading ||
							amount === 0 ||
							cardNumber.length !== 16 ||
							!cardOwner
						}
						style={{ fontSize: 18 }}
						onClick={transfer}
					>
						<Text>{buttonText}</Text>
					</Button>
				</StyledButtonContainer>
			</StyledContainer>
		</div>
	);
};

export { AppBankTransfer };
